import { useEffect, useRef, useState } from "react"
import axios from "axios"
import "../styles/compra.css"

const API_URL = "https://api.ticketdovaqueiro.com"

const CompraMapaCompartilhado = ({ token }) => {
  const [sessao, setSessao] = useState(null)
  const [evento, setEvento] = useState(null)
  const [selecoes, setSelecoes] = useState([])
  const [reservadas, setReservadas] = useState([])
  const [diaAtivo, setDiaAtivo] = useState(null)
  const [slotAtivo, setSlotAtivo] = useState(null)
  const [carregando, setCarregando] = useState(true)
  const [mensagem, setMensagem] = useState("")
  const ultimaSelecaoRemota = useRef("")
  const envioPendente = useRef(false)
  const ultimaAtualizacaoReservadas = useRef(0)

  const carregarMapa = async () => {
    const response = await axios.get(`${API_URL}/api/pedidos/mapa-compartilhado/${token}`)
    const sessaoAtual = response.data.sessao
    const eventoAtual = response.data.evento
    setSessao(sessaoAtual)
    setEvento(eventoAtual)
    setDiaAtivo((atual) => atual || eventoAtual.categorias?.[0]?.dias?.[0]?.nome || null)

    const remotas = sessaoAtual.selecoes || []
    const assinatura = JSON.stringify(remotas.map(({ slot, dia, numero }) => ({
      slot: Number(slot), dia, numero: Number(numero),
    })).sort((a, b) => a.slot - b.slot))
    if (!envioPendente.current && assinatura !== ultimaSelecaoRemota.current) {
      ultimaSelecaoRemota.current = assinatura
      setSelecoes(remotas.map((item) => ({ ...item, slot: Number(item.slot), numero: Number(item.numero) })))
      const primeiroVazio = Array.from({ length: sessaoAtual.quantidade }, (_, i) => i)
        .find((slot) => !remotas.some((item) => Number(item.slot) === slot))
      setSlotAtivo(primeiroVazio ?? null)
    }

    if (Date.now() - ultimaAtualizacaoReservadas.current > 7000) {
      const bloqueadas = await axios.get(
        `${API_URL}/api/pedidos/senhas-reservadas/${eventoAtual._id}/${encodeURIComponent(sessaoAtual.categoria)}`
      )
      setReservadas(Array.isArray(bloqueadas.data) ? bloqueadas.data : [])
      ultimaAtualizacaoReservadas.current = Date.now()
    }
  }

  useEffect(() => {
    let ativo = true
    const carregar = async () => {
      try {
        await carregarMapa()
        if (ativo) setMensagem("")
      } catch (err) {
        if (ativo) {
          setMensagem(err.response?.data?.mensagem || "Este link não está disponível.")
          if (err.response?.status === 404) {
            setSessao(null)
            setEvento(null)
          }
        }
      } finally {
        if (ativo) setCarregando(false)
      }
    }
    carregar()
    const timer = window.setInterval(carregar, 1400)
    return () => { ativo = false; window.clearInterval(timer) }
  }, [token])

  useEffect(() => {
    if (!sessao || carregando) return
    const assinatura = JSON.stringify(selecoes.slice().sort((a, b) => a.slot - b.slot))
    if (assinatura === ultimaSelecaoRemota.current) return
    envioPendente.current = true
    const timer = window.setTimeout(async () => {
      try {
        const response = await axios.put(`${API_URL}/api/pedidos/mapa-compartilhado/${token}`, { selecoes })
        const remotas = response.data.sessao.selecoes || []
        ultimaSelecaoRemota.current = JSON.stringify(remotas.map(({ slot, dia, numero }) => ({
          slot: Number(slot), dia, numero: Number(numero),
        })).sort((a, b) => a.slot - b.slot))
        setMensagem("")
      } catch (err) {
        setMensagem(err.response?.data?.mensagem || "Não foi possível atualizar a seleção. Tente outro número.")
        envioPendente.current = false
        try { await carregarMapa() } catch (_) {}
      } finally {
        envioPendente.current = false
      }
    }, 180)
    return () => window.clearTimeout(timer)
  }, [selecoes, sessao, carregando, token])

  const categoria = evento?.categorias?.find((item) => item.nome === sessao?.categoria)
  const dia = categoria?.dias?.find((item) => item.nome === diaAtivo)
  const numeros = dia
    ? Array.from({ length: dia.senhaFinal - dia.senhaInicial + 1 }, (_, i) => dia.senhaInicial + i)
      .filter((numero) => !(dia.senhasExcluidas || []).map(Number).includes(numero))
    : []
  const isOcupada = (numero) => reservadas.some((item) =>
    Number(typeof item === "object" ? item.numero : item) === Number(numero)
  )

  const escolherNumero = (numero) => {
    if (isOcupada(numero)) return
    const jaSelecionada = selecoes.find((item) => item.numero === numero && item.dia === diaAtivo)
    if (jaSelecionada) { setSlotAtivo(jaSelecionada.slot); return }

    const slot = slotAtivo ?? Array.from({ length: sessao.quantidade }, (_, i) => i)
      .find((i) => !selecoes.some((item) => item.slot === i))
    if (slot === undefined || slot === null) {
      setMensagem("Você já escolheu a quantidade definida. Toque em uma senha acima para trocá-la.")
      return
    }

    const novas = selecoes.filter((item) => item.slot !== slot)
    novas.push({ slot, dia: diaAtivo, numero: Number(numero) })
    novas.sort((a, b) => a.slot - b.slot)
    setSelecoes(novas)
    setSlotAtivo(Array.from({ length: sessao.quantidade }, (_, i) => i)
      .find((i) => !novas.some((item) => item.slot === i)) ?? null)
  }

  const removerSelecao = (slot) => {
    setSelecoes((prev) => prev.filter((item) => item.slot !== slot))
    setSlotAtivo(slot)
  }

  if (carregando) return (
    <main className="compra-container3 compra-passo2-app app-shared-customer">
      <div className="app-loading-map"><div className="loading-spinner"/><span>Abrindo mapa compartilhado…</span></div>
    </main>
  )

  if (!sessao || !evento || !categoria) return (
    <main className="compra-container3 compra-passo2-app app-shared-customer">
      <div className="app-empty-state"><strong>Link indisponível</strong><span>{mensagem || "Este mapa expirou ou foi encerrado."}</span></div>
    </main>
  )

  return (
    <main className="compra-container3 compra-passo2-app app-shared-customer">
      <header className="app-map-header app-shared-customer-header">
        <h1>Escolha {sessao.quantidade} {sessao.quantidade === 1 ? "senha" : "senhas"}</h1>
      </header>
      <div className="app-customer-event">
        <strong>{evento.nome}</strong><span>{sessao.categoria}</span>
        <small>Toque nos números livres; a equipe verá sua escolha na hora.</small>
      </div>
      <section className="app-slots-area" aria-label="Senhas escolhidas">
        <div className="app-selection-heading"><div><strong>Suas escolhas</strong><small>{selecoes.length} de {sessao.quantidade} selecionadas</small></div></div>
        <div className="app-slots-scroll">
          {Array.from({ length: sessao.quantidade }, (_, slot) => {
            const escolha = selecoes.find((item) => item.slot === slot)
            return (
              <div key={slot} className={`app-slot-card ${slotAtivo === slot ? "ativo" : ""} ${escolha ? "preenchido" : ""}`}>
                <button type="button" className="app-customer-slot-select" onClick={() => setSlotAtivo(slot)}>
                  <span className="app-slot-label">Senha {slot + 1}</span>
                  {escolha
                    ? <><strong className="app-slot-numero">{escolha.numero}</strong><small>{escolha.dia}</small></>
                    : <em>{slotAtivo === slot ? "escolha no mapa" : "aguardando"}</em>}
                </button>
                {escolha && <button type="button" className="app-slot-remove" aria-label={`Remover senha ${escolha.numero}`} onClick={() => removerSelecao(slot)}>×</button>}
              </div>
            )
          })}
        </div>
      </section>
      <nav className="app-day-tabs" aria-label="Dias do evento">
        {(categoria.dias || []).map((item) => (
          <button type="button" key={item.nome} className={`app-day-tab ${diaAtivo === item.nome ? "ativo" : ""}`} onClick={() => setDiaAtivo(item.nome)}>{item.nome}</button>
        ))}
      </nav>
      {mensagem && <div className="app-shared-feedback" role="status">{mensagem}</div>}
      <section className="app-map-area" aria-label="Mapa de senhas">
        <div className="app-number-grid">
          {numeros.map((numero) => {
            const reservada = isOcupada(numero)
            const escolha = selecoes.find((item) => item.numero === numero && item.dia === diaAtivo)
            return <button key={numero} type="button" disabled={reservada} className={`app-number-box ${reservada ? "ocupada" : "livre"} ${escolha ? "sua-escolha" : ""}`} onClick={() => escolherNumero(numero)}>{numero}</button>
          })}
        </div>
      </section>
      <section className="app-legend-bar"><span><i className="livre"/>Livre</span><span><i className="ocupada"/>Ocupada</span><span><i className="sua"/>Sua escolha</span></section>
      <footer className="app-bottom-bar app-customer-bottom">
        <div><strong>{selecoes.length} de {sessao.quantidade}</strong><small>A equipe está acompanhando</small></div>
        <span className="app-live-indicator">● AO VIVO</span>
      </footer>
    </main>
  )
}

export default CompraMapaCompartilhado
